import {
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useState, useEffect } from "react";
import { ResponsiveRow } from "../../../../constants/layout.constants";
import ModalContainerModule from "../../../modules/modalContainer";
import { getMeterReadings } from "../../../../services/meters.service";
import { isEmptyString, isNullUndefined, tryParseFloat } from "../../../../util/util";
import { issueWarnMessage } from "../../../../actions/message";

export default function MeterSwapModal(props) {
  /**
   *
   * REQUIRED PROPS
   *
   * swapModal - boolean to show/hide modal
   * setSwapModal - function to close modal
   * mode - "swap" or "remove"
   * availableMeters - array of meter options {value, label} (required for swap mode)
   * selectedMeterId - the meter ID being swapped/removed
   * selectedMeterName - the meter name being swapped/removed (for display)
   * selectedNewMeterId - the new meter ID (for swap mode)
   * setSelectedNewMeterId - function to set new meter ID (for swap mode)
   * onSwapConfirm - function to handle swap confirmation (receives newMeterId for swap, no params for remove)
   * onRemoveConfirm - function to handle remove confirmation (no params)
   *
   */

  const mode = props.mode || "swap";
  const isRemoveMode = mode === "remove";
  const isAddMode = mode === "add";

  // State for meter readings
  const [finalReadingAmount, setFinalReadingAmount] = useState("");
  const [oldMeterFinalReadingAmount, setOldMeterFinalReadingAmount] = useState("");
  const [newMeterInitialReadingAmount, setNewMeterInitialReadingAmount] = useState("");
  const [readingDate, setReadingDate] = useState(dayjs());
  const [latestReading, setLatestReading] = useState(null);
  const [readingErrors, setReadingErrors] = useState({});

  // Fetch latest readings when modal opens or meter changes
  useEffect(() => {
    if (!props.swapModal) {
      // Reset state when modal closes
      setFinalReadingAmount("");
      setOldMeterFinalReadingAmount("");
      setNewMeterInitialReadingAmount("");
      setReadingDate(dayjs());
      setLatestReading(null);
      setReadingErrors({});
      return;
    }

    const fetchReadings = async () => {
      if (props.selectedMeterId) {
        try {
          const response = await getMeterReadings(props.selectedMeterId);
          setLatestReading(response.lastMeterReading)
        } catch (error) {
          console.error("Error fetching meter readings:", error);
        }
      }
    };

    fetchReadings();
  }, [props.swapModal, props.selectedMeterId, props.selectedNewMeterId, isRemoveMode, isAddMode]);

  // Validate reading
  const validateReading = (meterId, readingAmount, latestReading) => {
    if (isEmptyString(readingAmount) || isNullUndefined(readingAmount)) {
      return { valid: false, error: "Reading amount is required" };
    }

    const amount = tryParseFloat(readingAmount);
    if (isNullUndefined(amount) || amount < 0) {
      return { valid: false, error: "Reading amount must be a valid positive number" };
    }

    if (latestReading !== undefined && latestReading !== null) {
      if (amount < latestReading) {
        return {
          valid: false,
          error: `Reading amount (${amount}) must be greater than or equal to latest reading (${latestReading})`,
        };
      }
    }

    return { valid: true, error: null };
  };

  const handleConfirm = () => {
    let isValid = true;
    const errors = {};

    if (isRemoveMode) {
      // Validate final reading for remove
      const validation = validateReading(
        props.selectedMeterId,
        finalReadingAmount,
        latestReading
      );
      if (!validation.valid) {
        isValid = false;
        errors.finalReading = validation.error;
        issueWarnMessage(validation.error);
      }
    } else if (isAddMode) {
      // Validate initial reading for add
      if (!props.selectedNewMeterId) {
        isValid = false;
        issueWarnMessage("Please select a meter to add");
      } else {
        const validation = validateReading(
          props.selectedNewMeterId,
          newMeterInitialReadingAmount,
          latestReading
        );
        if (!validation.valid) {
          isValid = false;
          errors.newMeterInitialReading = validation.error;
          issueWarnMessage(validation.error);
        }
      }
    }

    if (!isValid) {
      setReadingErrors(errors);
      return;
    }

    // Prepare reading data
    const readingData = {
      date: readingDate ? readingDate.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
    };

    if (isRemoveMode) {
      if (props.onRemoveConfirm) {
        props.onRemoveConfirm({
          finalMeterReading: {
            meterId: props.selectedMeterId,
            amount: tryParseFloat(finalReadingAmount),
            date: readingData.date,
          },
        });
      }
    } else if (isAddMode) {
      if (props.selectedNewMeterId && props.onAddConfirm) {
        props.onAddConfirm(props.selectedNewMeterId, {
          initialMeterReading: {
            meterId: props.selectedNewMeterId,
            amount: tryParseFloat(newMeterInitialReadingAmount),
            date: readingData.date,
          },
        });
      }
    } else {
      if (props.selectedNewMeterId && props.onSwapConfirm) {
        props.onSwapConfirm(props.selectedNewMeterId, {
          meterReadings: [
            {
              meterId: props.selectedMeterId,
              amount: tryParseFloat(oldMeterFinalReadingAmount),
              date: readingData.date,
            },
            {
              meterId: props.selectedNewMeterId,
              amount: tryParseFloat(newMeterInitialReadingAmount),
              date: readingData.date,
            },
          ],
        });
      }
    }
    props.setSwapModal(false);
  };

  return (
    <ModalContainerModule
      size="xs"
      accept={handleConfirm}
      cancel={() => props.setSwapModal(false)}
      openModal={props.swapModal}
      modalTitle={isRemoveMode ? "Remove Meter" : isAddMode ? "Add Meter" : "Swap Meter"}
      acceptButtonText={isRemoveMode ? "Remove" : isAddMode ? "Add" : "Swap"}
      acceptDisabled={
        (!isRemoveMode && !props.selectedNewMeterId) ||
        (isRemoveMode && isEmptyString(finalReadingAmount)) ||
        (!isRemoveMode && !isAddMode && (isEmptyString(oldMeterFinalReadingAmount) || isEmptyString(newMeterInitialReadingAmount))) ||
        (isAddMode && isEmptyString(newMeterInitialReadingAmount))
      }
    >
      <ResponsiveRow sx={{ height: "auto", width: "100%", padding: "0", margin: "0" }}>
        {isRemoveMode ? (
          <>
            <Alert sx={{ width: "100%", marginBottom: "12px" }} severity="warning">
              Are you sure you want to remove this meter from the billing? All active charges for this meter will be terminated.
            </Alert>
            <Typography sx={{ width: "100%", marginBottom: "12px", fontSize: "14px" }}>
              <strong>Meter to Remove:</strong> {props.selectedMeterName || props.selectedMeterId}
            </Typography>
            {latestReading !== undefined && (
              <Typography sx={{ width: "100%", marginBottom: "8px", fontSize: "14px" }}>
                <strong>Last Meter Reading:</strong> {latestReading}
              </Typography>
            )}
            <FormControl sx={{ width: { xs: "100%", lg: "50%" }, marginBottom: "12px" }}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="en-gb"
              >
                <DatePicker
                  sx={{ width: { xs: "100%", lg: "90%" } }}
                  label="Reading Date"
                  value={readingDate}
                  maxDate={dayjs()}
                  onChange={(newDate) => setReadingDate(newDate)}
                />
              </LocalizationProvider>
            </FormControl>
            <FormControl sx={{ width: { xs: "100%", lg: "50%" }, marginBottom: "12px" }}>
              <InputLabel required>Final Reading</InputLabel>
              <OutlinedInput
                label="Final Reading"
                value={finalReadingAmount}
                onChange={(event) => {
                  setFinalReadingAmount(event.target.value);
                  if (readingErrors.finalReading) {
                    setReadingErrors({ ...readingErrors, finalReading: null });
                  }
                }}
                error={!!readingErrors.finalReading}
                required
              />
            </FormControl>
            {readingErrors.finalReading && (
              <Typography sx={{ width: "100%", color: "error", fontSize: "12px", marginBottom: "8px" }}>
                {readingErrors.finalReading}
              </Typography>
            )}
            <Alert sx={{ width: "100%", marginBottom: "0" }} severity="info">
              This action cannot be undone. The meter will be detached from this billing, but the billing will remain active with the remaining meters.
            </Alert>
          </>
        ) : isAddMode ? (
          <>
            <Alert sx={{ width: "100%", marginBottom: "8px" }} severity="info">
              Select a meter to add to this billing. You will need to add charges for this meter manually after it is added.
            </Alert>
            <FormControl
              sx={{
                width: { xs: "100%", lg: "100%" },
                marginBottom: "12px",
                marginTop: "0",
              }}
            >
              <InputLabel>Select Meter to Add</InputLabel>
              <Select
                label="Select Meter to Add"
                sx={{ width: { xs: "100%", lg: "100%" } }}
                id={"addMeter"}
                value={props.selectedNewMeterId || ""}
                onChange={async (event) => {
                  if (props.setSelectedNewMeterId) {
                    props.setSelectedNewMeterId(event.target.value);
                    // Fetch readings for newly selected meter
                    if (event.target.value) {
                      try {
                        const response = await getMeterReadings(event.target.value);
                        setLatestReading(response.lastMeterReading)
                      } catch (error) {
                        console.error("Error fetching meter readings:", error);
                      }
                    }
                  }
                }}
              >
                {props.availableMeters && props.availableMeters.length > 0 ? (
                  props.availableMeters.map(function (meter, i) {
                    return (
                      <MenuItem key={meter.value} value={meter.value}>
                        {meter.label}
                      </MenuItem>
                    );
                  }, this)
                ) : (
                  <MenuItem sx={{ width: "100%" }}>No Meters Available</MenuItem>
                )}
              </Select>
            </FormControl>
            {latestReading !== undefined && (
              <Typography sx={{ width: "100%", marginBottom: "8px", fontSize: "14px" }}>
                <strong>Last Meter Reading:</strong> {latestReading}
              </Typography>
            )}
            <FormControl sx={{ width: { xs: "100%", lg: "50%" }, marginBottom: "12px" }}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="en-gb"
              >
                <DatePicker
                  sx={{ width: { xs: "100%", lg: "90%" } }}
                  label="Reading Date"
                  value={readingDate}
                  maxDate={dayjs()}
                  onChange={(newDate) => setReadingDate(newDate)}
                />
              </LocalizationProvider>
            </FormControl>
            <FormControl sx={{ width: { xs: "100%", lg: "50%" }, marginBottom: "12px" }}>
              <InputLabel required>Current Reading</InputLabel>
              <OutlinedInput
                label="Current Reading"
                value={newMeterInitialReadingAmount}
                onChange={(event) => {
                  setNewMeterInitialReadingAmount(event.target.value);
                  if (readingErrors.newMeterInitialReading) {
                    setReadingErrors({ ...readingErrors, newMeterInitialReading: null });
                  }
                }}
                error={!!readingErrors.newMeterInitialReading}
                required
              />
            </FormControl>
            {readingErrors.newMeterInitialReading && (
              <Typography sx={{ width: "100%", color: "error", fontSize: "12px", marginBottom: "8px" }}>
                {readingErrors.newMeterInitialReading}
              </Typography>
            )}
          </>
        ) : null}
      </ResponsiveRow>
    </ModalContainerModule>
  );
}


